import { useMemo, useState } from 'react';
import type { EnrichedFeature } from '@/hooks/useGeoLayer';
import {
  countExportableFeatures,
  exportFeaturesToGeoJsonFile,
  fetchFreshGeoLayerFeatures,
} from '@/lib/geojson-export';
import { getLayerDisplayName } from '@/screens/TourismMap/layer-options';
import styles from './AgriTourMapScreen.module.css';

interface AgriTourMapToolbarProps {
  title: string;
  layerId: string;
  features: EnrichedFeature[];
  mapLoading?: boolean;
  totalCount: number;
  withGeoCount: number;
  polygonCount: number;
}

export function AgriTourMapToolbar({
  title,
  layerId,
  features,
  mapLoading = false,
  totalCount,
  withGeoCount,
  polygonCount,
}: AgriTourMapToolbarProps) {
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const exportableCount = useMemo(() => countExportableFeatures(features), [features]);

  async function handleExportGeoJson() {
    setNotice(null);
    setError(null);

    let exportFeatures = features;
    try {
      exportFeatures = await fetchFreshGeoLayerFeatures(layerId);
    } catch (err) {
      setError((err as Error).message);
      return;
    }

    const { filenames, stats } = await exportFeaturesToGeoJsonFile(exportFeatures, {
      layerId,
      layerDisplayName: getLayerDisplayName(layerId),
    });

    if (stats.featureCount === 0) {
      setError('אין פריטים עם מיקום לייצוא.');
      return;
    }

    const polygonTotal = stats.polygonCount + stats.multiPolygonCount;
    if (filenames.length > 1 && polygonTotal > 0) {
      setNotice(`הורדתם 2 קבצים (${polygonTotal} מצוירים, ${stats.pointCount} נקודות).`);
      return;
    }

    if (polygonTotal > 0) {
      setNotice(`הורד קובץ עם ${polygonTotal} מצוירים.`);
    } else {
      setNotice(`הורד קובץ עם ${stats.pointCount} נקודות.`);
    }
  }

  return (
    <div className={styles.toolbar} dir="rtl">
      <div className={styles.toolbarMain}>
        <div className={styles.toolbarStart}>
          <h1 className={styles.toolbarTitle}>{title}</h1>
          <div className={styles.statPills} aria-label="סיכום בקשות">
            <span className={styles.statPill}>
              <strong>{totalCount}</strong> בקשות
            </span>
            <span className={styles.statPill}>
              <strong>{withGeoCount}</strong> עם מיקום
            </span>
            {polygonCount > 0 ? (
              <span className={styles.statPill}>
                <strong>{polygonCount}</strong> מצוירים
              </span>
            ) : null}
          </div>
        </div>

        <div className={styles.toolbarActions}>
          <button
            type="button"
            className={styles.exportBtn}
            onClick={() => void handleExportGeoJson()}
            disabled={mapLoading || exportableCount === 0}
            title="הורדת GeoJSON"
          >
            ייצוא GeoJSON
          </button>
        </div>
      </div>

      {error ? (
        <p className={styles.toolbarNotice} data-tone="error" role="alert">
          {error}
        </p>
      ) : null}
      {notice && !error ? (
        <p className={styles.toolbarNotice} data-tone="info" role="status">
          {notice}
        </p>
      ) : null}
    </div>
  );
}
